/**
 * Retry Orchestrator - Exponential backoff retry for cloud sync operations.
 *
 * RETRY POLICY:
 *   Max attempts:  3
 *   Delays:        2s → 8s → 32s (exponential: base × 4^attempt)
 *   Jitter:        ±20% to prevent thundering herd
 *
 * ABORT CONDITIONS (no retry):
 *   - HTTP 401/403 (auth/permission errors - retrying won't help)
 *   - AbortError (explicit cancellation)
 *
 * After exhausting all attempts, calls onExhausted and does not throw.
 */

import { logger } from "@/system/logger";

const BASE_DELAY_MS  = 2_000;
const MAX_ATTEMPTS   = 3;
const JITTER_FACTOR  = 0.2; // ±20%

// Errors that should NOT be retried
const FATAL_CODES = new Set(["PGRST301", "42501"]); // auth / permission errors

function withJitter(ms: number): number {
  const jitter = ms * JITTER_FACTOR;
  return ms + (Math.random() * 2 - 1) * jitter;
}

function delayForAttempt(attempt: number): number {
  // attempt 0 → 2s, attempt 1 → 8s, attempt 2 → 32s
  return withJitter(BASE_DELAY_MS * Math.pow(4, attempt));
}

export interface RetryContext {
  projectId:   string;
  sequenceNum: number;
}

export interface RetryCallbacks {
  onAttempt?:   (ctx: RetryContext, attempt: number) => void;
  onSuccess?:   (ctx: RetryContext, attempt: number) => void;
  onFailure?:   (ctx: RetryContext, attempt: number, error: string, nextDelayMs: number) => void;
  onExhausted?: (ctx: RetryContext, totalAttempts: number) => void;
}

export class RetryOrchestrator {
  async execute<T>(
    ctx:       RetryContext,
    operation: () => Promise<T>,
    callbacks: RetryCallbacks = {}
  ): Promise<T | null> {

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      callbacks.onAttempt?.(ctx, attempt);

      try {
        const result = await operation();
        callbacks.onSuccess?.(ctx, attempt);
        return result;

      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const code    = (err as Record<string, unknown>)?.code as string | undefined;

        // Fatal - do not retry
        if (
          (err instanceof DOMException && err.name === "AbortError") ||
          (code && FATAL_CODES.has(code))
        ) {
          logger.error("RetryOrchestrator: fatal error - not retrying", {
            projectId: ctx.projectId, attempt, error: message, code,
          });
          callbacks.onExhausted?.(ctx, attempt + 1);
          return null;
        }

        const isLast     = attempt === MAX_ATTEMPTS - 1;
        const nextDelayMs = isLast ? 0 : Math.round(delayForAttempt(attempt + 1));

        callbacks.onFailure?.(ctx, attempt, message, nextDelayMs);

        if (isLast) {
          logger.error("RetryOrchestrator: all attempts exhausted", {
            projectId: ctx.projectId,
            totalAttempts: MAX_ATTEMPTS,
          });
          callbacks.onExhausted?.(ctx, MAX_ATTEMPTS);
          return null;
        }

        logger.warn("RetryOrchestrator: attempt failed - retrying", {
          projectId: ctx.projectId, attempt, error: message, nextDelayMs,
        });

        await new Promise(resolve => setTimeout(resolve, nextDelayMs));
      }
    }

    return null;
  }
}
